/**
 * AuthContext — extracted from UserContext
 *
 * Holds ONLY authentication state:
 * - user object and isAuthenticated flag
 * - isLoading (initial auth check)
 * - login / logout / register / refreshToken
 * - impersonation (loginAs / exitImpersonation)
 *
 * Does NOT hold: wallet, KYC status, Stripe state.
 *
 * Mount order requirement:
 *   <AuthProvider>        ← outermost
 *     <KYCProvider>       ← uses useAuth() internally
 *       ...rest of app
 *     </KYCProvider>
 *   </AuthProvider>
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { User } from '../types';
import { authApi } from '../services/api';
import { clearAuthStorage, clearUserScopedStorage } from '../config/authRoutes';
import { toast } from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Impersonation state
// ---------------------------------------------------------------------------
export interface ImpersonationState {
  targetUserId: string;
  targetEmail: string;
  sandboxTenantId: string;
  originalToken: string;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sandboxTenantId: string | null;
  impersonating: ImpersonationState | null;

  login(
    email: string,
    password: string,
    totpCode?: string
  ): Promise<boolean | 'requires2FA'>;

  register(data: {
    email: string;
    password: string;
    confirmPassword: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    businessName?: string;
    businessAddress?: string;
    kyc?: unknown;
    profile?: unknown;
  }): Promise<boolean>;

  logout(): void;

  refreshToken(): Promise<void>;

  impersonateUser(targetUserId: string, targetEmail: string): Promise<boolean>;

  stopImpersonation(): void;

  /** Re-fetch and sync the user profile from the backend. */
  refreshUser(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'SET_SANDBOX_TENANT'; payload: string | null }
  | { type: 'SET_IMPERSONATING'; payload: ImpersonationState | null }
  | { type: 'LOGOUT' };

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sandboxTenantId: string | null;
  impersonating: ImpersonationState | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  sandboxTenantId: null,
  impersonating: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
      };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    case 'SET_SANDBOX_TENANT':
      return { ...state, sandboxTenantId: action.payload };
    case 'SET_IMPERSONATING':
      return { ...state, impersonating: action.payload };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        sandboxTenantId: null,
        impersonating: null,
      };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/** Normalize the raw API user object (camelCase from backend) to our frontend User type. */
function normalizeUser(raw: Record<string, unknown>): User {
  return {
    id: raw.id as string,
    email: raw.email as string,
    role: (raw.role as User['role']) || 'user',
    kyc_status:
      (raw.kycStatus as User['kyc_status']) ||
      (raw.kyc_status as User['kyc_status']) ||
      'not_started',
    kyc_verified_at:
      (raw.kycVerifiedAt as string | undefined) ||
      (raw.kyc_verified_at as string | undefined),
    kyc_session_id:
      (raw.kycSessionId as string | undefined) ||
      (raw.kyc_session_id as string | undefined),
    wallet_address:
      (raw.walletAddress as string | undefined) ||
      (raw.wallet_address as string | undefined),
    email_verified:
      (raw.emailVerified as boolean) || (raw.email_verified as boolean) || false,
    two_factor_enabled:
      (raw.twoFactorEnabled as boolean) ||
      (raw.two_factor_enabled as boolean) ||
      false,
    first_name:
      (raw.firstName as string | undefined) || (raw.first_name as string | undefined),
    last_name:
      (raw.lastName as string | undefined) || (raw.last_name as string | undefined),
    phone: raw.phone as string | undefined,
    business_name:
      (raw.businessName as string | undefined) ||
      (raw.business_name as string | undefined),
    business_address:
      (raw.businessAddress as string | undefined) ||
      (raw.business_address as string | undefined),
    address_line1:
      (raw.addressLine1 as string | undefined) ||
      (raw.address_line1 as string | undefined),
    address_line2:
      (raw.addressLine2 as string | undefined) ||
      (raw.address_line2 as string | undefined),
    city: raw.city as string | undefined,
    state: raw.state as string | undefined,
    zip_code:
      (raw.zipCode as string | undefined) || (raw.zip_code as string | undefined),
    country: raw.country as string | undefined,
    created_at: raw.created_at as string,
    updated_at: raw.updated_at as string,
  };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  // The signed-in user's id, for the sign-out's own-keys-only cleanup (logout is a stable callback).
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = state.user?.id || null;

  // -------------------------------------------------------------------------
  // Initialize auth from stored token on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        // Decode token to check expiry
        const decoded = jwtDecode<Record<string, unknown>>(token);
        const expiryTime = (decoded.exp as number) * 1000;
        const now = Date.now();
        const GRACE_PERIOD = 5 * 60 * 1000;

        if (expiryTime + GRACE_PERIOD < now) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('tokenExpiry');
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        // Restore sandbox tenant
        if (decoded.sandboxTenantId) {
          dispatch({
            type: 'SET_SANDBOX_TENANT',
            payload: decoded.sandboxTenantId as string,
          });
        }

        // Fetch profile with retry
        let response = await authApi.getProfile();
        let retries = 0;
        while (!response.success && retries < 2) {
          await delay(1000 * Math.pow(2, retries));
          response = await authApi.getProfile();
          retries++;
        }

        if (response.success && response.data) {
          const user = normalizeUser(response.data as unknown as Record<string, unknown>);
          dispatch({ type: 'SET_USER', payload: user });
          localStorage.setItem('tokenExpiry', expiryTime.toString());
        } else {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tokenExpiry');
        }
      } catch {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiry');
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // -------------------------------------------------------------------------
  // Auto-refresh token before expiry
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const doRefresh = async () => {
      try {
        const res = await authApi.refreshToken();
        const data = res.data as Record<string, unknown>;
        const newToken = (data?.token || data?.accessToken) as string | undefined;
        if (res.success && newToken) {
          localStorage.setItem('authToken', newToken);
          const newRefresh = data?.refreshToken as string | undefined;
          if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
          const decoded = jwtDecode<Record<string, unknown>>(newToken);
          localStorage.setItem(
            'tokenExpiry',
            String((decoded.exp as number) * 1000)
          );
        }
      } catch {
        // Keep old token — retry in 1 minute
        setTimeout(doRefresh, 60_000);
      }
    };

    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const decoded = jwtDecode<Record<string, unknown>>(token);
      const expiryTime = (decoded.exp as number) * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;

      if (timeUntilExpiry < 5 * 60 * 1000) {
        doRefresh();
        return;
      }

      const refreshAt = timeUntilExpiry - 5 * 60 * 1000;
      const timer = setTimeout(doRefresh, refreshAt);
      return () => clearTimeout(timer);
    } catch {
      const interval = setInterval(doRefresh, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [state.isAuthenticated]);

  // -------------------------------------------------------------------------
  // login
  // -------------------------------------------------------------------------
  const login = useCallback(
    async (
      email: string,
      password: string,
      totpCode?: string
    ): Promise<boolean | 'requires2FA'> => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const response = await authApi.login({
          email,
          password,
          ...(totpCode ? { totpCode } : {}),
        });

        const raw = response as unknown as Record<string, unknown>;
        if (raw.requires2FA) {
          dispatch({ type: 'SET_LOADING', payload: false });
          return 'requires2FA';
        }

        if (response.success && response.data) {
          const backendData = response.data as Record<string, unknown>;
          const rawUser = backendData.user as Record<string, unknown>;
          const token =
            (backendData.accessToken as string) || (backendData.token as string);

          const normalizedUser = normalizeUser(rawUser);

          localStorage.setItem('authToken', token);
          const refreshTkn = backendData.refreshToken as string | undefined;
          if (refreshTkn) localStorage.setItem('refreshToken', refreshTkn);
          try {
            const decoded = jwtDecode<Record<string, unknown>>(token);
            const expiryTime = (decoded.exp as number) * 1000;
            localStorage.setItem('tokenExpiry', String(expiryTime));
            if (decoded.sandboxTenantId) {
              dispatch({
                type: 'SET_SANDBOX_TENANT',
                payload: decoded.sandboxTenantId as string,
              });
            }
          } catch {
            // non-critical
          }

          dispatch({ type: 'SET_USER', payload: normalizedUser });
          dispatch({ type: 'SET_LOADING', payload: false });
          return true;
        } else {
          toast.error((response.message as string) || 'Invalid email or password');
          return false;
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Login failed. Please try again.';
        toast.error(message);
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    []
  );

  // -------------------------------------------------------------------------
  // register
  // -------------------------------------------------------------------------
  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      confirmPassword: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      businessName?: string;
      businessAddress?: string;
      kyc?: unknown;
      profile?: unknown;
    }): Promise<boolean> => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const response = await authApi.register(data);
        const raw = response as unknown as Record<string, unknown>;
        const backendUser = raw.user as Record<string, unknown> | undefined;
        const token = raw.token as string | undefined;

        if (backendUser && token) {
          const normalizedUser = normalizeUser(backendUser);
          // New registrations are not yet email-verified
          normalizedUser.email_verified = false;

          localStorage.setItem('authToken', token);
          try {
            const decoded = jwtDecode<Record<string, unknown>>(token);
            localStorage.setItem(
              'tokenExpiry',
              String((decoded.exp as number) * 1000)
            );
          } catch {
            // non-critical
          }

          dispatch({ type: 'SET_USER', payload: normalizedUser });
          toast.success('Account created! Redirecting to verification...');
          return true;
        } else {
          const errorMsg =
            (raw.message as string) || (raw.error as string) || 'Registration failed';
          toast.error(errorMsg);
          return false;
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Registration failed';
        toast.error(message);
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    []
  );

  // -------------------------------------------------------------------------
  // logout
  // -------------------------------------------------------------------------
  const logout = useCallback(async () => {
    const signingOut = userIdRef.current;
    try {
      await authApi.logout();
    } catch {
      // ignore
    } finally {
      // The same cleanup UserContext performs — see config/authRoutes. The two
      // lists diverged while both contexts coexist, which is precisely when one
      // contract matters most.
      clearAuthStorage();
      // A deliberate sign-out also clears this person's own unsent note drafts (PHI) on this machine.
      clearUserScopedStorage(signingOut);
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  // -------------------------------------------------------------------------
  // refreshToken
  // -------------------------------------------------------------------------
  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const res = await authApi.refreshToken();
      const data = res.data as Record<string, unknown> | undefined;
      const newToken = (data?.token || data?.accessToken) as string | undefined;
      if (res.success && newToken) {
        localStorage.setItem('authToken', newToken);
        const newRefresh = data?.refreshToken as string | undefined;
        if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
        const decoded = jwtDecode<Record<string, unknown>>(newToken);
        localStorage.setItem(
          'tokenExpiry',
          String((decoded.exp as number) * 1000)
        );
      }
    } catch (err) {
      console.error('[AuthContext] refreshToken failed:', err);
    }
  }, []);

  // -------------------------------------------------------------------------
  // refreshUser — re-fetch profile from backend
  // -------------------------------------------------------------------------
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const response = await authApi.getProfile();
      if (response.success && response.data) {
        const user = normalizeUser(response.data as unknown as Record<string, unknown>);
        dispatch({ type: 'SET_USER', payload: user });
      }
    } catch (err) {
      console.error('[AuthContext] refreshUser failed:', err);
    }
  }, []);

  // -------------------------------------------------------------------------
  // impersonateUser (admin only — production safe)
  // -------------------------------------------------------------------------
  const impersonateUser = useCallback(
    async (targetUserId: string, targetEmail: string): Promise<boolean> => {
      try {
        const currentToken = localStorage.getItem('authToken');
        if (!currentToken) return false;

        const res = await fetch('/api/v1/admin/impersonate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentToken}`,
          },
          body: JSON.stringify({ targetUserId }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({})) as Record<string, unknown>;
          toast.error((err.message as string) || (err.error as string) || 'Impersonation failed');
          return false;
        }

        const data = await res.json() as Record<string, unknown>;
        const impToken = data.token as string;
        const decoded = jwtDecode<Record<string, unknown>>(impToken);

        const impersonationState: ImpersonationState = {
          targetUserId,
          targetEmail,
          sandboxTenantId: '',
          originalToken: currentToken,
        };

        localStorage.setItem('authToken', impToken);
        localStorage.setItem('tokenExpiry', String((decoded.exp as number) * 1000));
        localStorage.setItem('impersonating_as', targetEmail);
        localStorage.setItem('original_admin_token', currentToken);

        dispatch({ type: 'SET_IMPERSONATING', payload: impersonationState });

        // Fetch impersonated user's profile so the UI renders as that user
        const profileRes = await fetch('/api/v1/auth/me', {
          headers: { Authorization: `Bearer ${impToken}` },
        });
        if (profileRes.ok) {
          const profileData = await profileRes.json() as Record<string, unknown>;
          const rawU = (profileData.data || profileData.user || profileData) as Record<string, unknown>;
          dispatch({ type: 'SET_USER', payload: normalizeUser(rawU) });
        }

        toast.success(`Now viewing portal as ${targetEmail}`);
        return true;
      } catch (err: unknown) {
        console.error('[AuthContext] impersonateUser error:', err);
        toast.error('Failed to switch user context');
        return false;
      }
    },
    []
  );

  // -------------------------------------------------------------------------
  // stopImpersonation
  // -------------------------------------------------------------------------
  const stopImpersonation = useCallback(() => {
    // Restore original token — fall back to localStorage if state was lost
    const originalToken =
      state.impersonating?.originalToken ||
      localStorage.getItem('original_admin_token') ||
      '';
    if (!originalToken) return;

    localStorage.setItem('authToken', originalToken);
    localStorage.removeItem('impersonating_as');
    localStorage.removeItem('original_admin_token');

    try {
      const decoded = jwtDecode<Record<string, unknown>>(originalToken);
      localStorage.setItem('tokenExpiry', String((decoded.exp as number) * 1000));
      dispatch({
        type: 'SET_SANDBOX_TENANT',
        payload: (decoded.sandboxTenantId as string) || null,
      });
    } catch {
      // non-critical
    }

    dispatch({ type: 'SET_IMPERSONATING', payload: null });

    fetch('/api/v1/auth/me', {
      headers: { Authorization: `Bearer ${originalToken}` },
    })
      .then(r => r.json())
      .then((profileData: unknown) => {
        const pd = profileData as Record<string, unknown>;
        const rawU = (pd.data || pd.user || pd) as Record<string, unknown>;
        dispatch({ type: 'SET_USER', payload: normalizeUser(rawU) });
      })
      .catch(() => null);

    toast.success('Returned to your admin session');
  }, [state.impersonating]);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------
  const value: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    sandboxTenantId: state.sandboxTenantId,
    impersonating: state.impersonating,
    login,
    register,
    logout,
    refreshToken,
    impersonateUser,
    stopImpersonation,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      '[AuthContext] useAuth() must be called inside <AuthProvider>. ' +
        'Wrap your component tree with <AuthProvider>.'
    );
  }
  return context;
}

export { AuthContext };
export default AuthContext;
